import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import LegacyLogo from "../../assets/legacy grey.png";
import ellipse from "../../assets/ellipse-1@2x.png";
import { useNavigate } from "react-router-dom";
import ProfileModal from "../Modals/ProfileModal";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { admin_base_url } from "../../Global";
import { useAuth } from "../../context/AuthContext";
import usePermission from "../../hooks/usePermission";
import { ROLE_LABELS, PERMISSIONS } from "../../adminConfig";

const Navbar = ({ tab, changeTab, onMenuClick = () => {} }) => {
  const navigate = useNavigate();
  const { user, can } = usePermission();
  const { refreshUser } = useAuth();

  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [currentlyPassword, setCurrentlyPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setUpdateOpen(false);
    setCurrentlyPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleMyAccount = () => {
    handleCloseMenu();
    const stored = JSON.parse(localStorage.getItem("user") || "{}");
    const u = user || stored;
    const fName = u.first_name || u.firstName || "";
    const lName = u.last_name || u.lastName || "";
    const email = u.email || "";

    setFirstName(fName);
    setLastName(lName);
    setUserEmail(email);

    setNewFirstName(fName);
    setNewLastName(lName);
    setNewUserEmail(email);

    setCurrentlyPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setUpdateOpen(false);
    setModalOpen(true);
  };

  const handleCancelEdit = () => {
    setNewFirstName(firstName);
    setNewLastName(lastName);
    setNewUserEmail(userEmail);
    setCurrentlyPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setUpdateOpen(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (newPassword && newPassword !== confirmNewPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword && newPassword === currentlyPassword) {
      toast.error("New password cannot be the same as the current password");
      return;
    }
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const res = await fetch(`${admin_base_url}/update-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          first_name: newFirstName,
          last_name: newLastName,
          email: newUserEmail,
          currentPassword: currentlyPassword,
          ...(newPassword && { newPassword }),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update profile");

      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const updatedUser = {
        ...storedUser,
        ...(data.data || {}),
        first_name: newFirstName,
        last_name: newLastName,
        email: newUserEmail,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      setFirstName(newFirstName);
      setLastName(newLastName);
      setUserEmail(newUserEmail);

      if (refreshUser) refreshUser();

      toast.success("Profile updated successfully!");
      handleCancelEdit();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const currentRole = user?.role || "SUPER_ADMIN";
  const roleLabel = ROLE_LABELS[currentRole] || currentRole;

  return (
    <>
      <ProfileModal isOpen={isModalOpen} onClose={closeModal}>
        <div className="space-y-4" style={{ color: "#000000" }}>
          <div className="flex justify-center mb-4">
            <img
              className="h-[3.25rem] w-[3.25rem] rounded-[50%] object-cover"
              loading="lazy"
              alt=""
              src={ellipse}
            />
          </div>

          {updateOpen ? (
            <form onSubmit={handleUpdate} className="space-y-3" style={{ textAlign: "left", color: "#000000" }}>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#000000" }}>First Name</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ color: "#000000", backgroundColor: "#ffffff" }}
                  type="text"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  placeholder="First Name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#000000" }}>Last Name</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ color: "#000000", backgroundColor: "#ffffff" }}
                  type="text"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  placeholder="Last Name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#000000" }}>Email</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ color: "#000000", backgroundColor: "#ffffff" }}
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="Email"
                  required
                />
              </div>
              <hr className="border-gray-200" />
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#000000" }}>New password</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ color: "#000000", backgroundColor: "#ffffff" }}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Leave blank to keep current"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#000000" }}>Confirm new password</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ color: "#000000", backgroundColor: "#ffffff" }}
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Repeat new password"
                />
              </div>
              <hr className="border-gray-200" />
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#000000" }}>
                  Current password <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ color: "#000000", backgroundColor: "#ffffff" }}
                  type="password"
                  value={currentlyPassword}
                  onChange={(e) => setCurrentlyPassword(e.target.value)}
                  placeholder="Required to save any changes"
                  required
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  className="flex-1 px-4 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-black font-medium focus:outline-none cursor-pointer"
                  style={{ color: "#000000" }}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 px-4 py-2 rounded-md border-none ${loading ? "bg-gray-400" : "bg-black hover:bg-gray-800"} text-white focus:outline-none cursor-pointer`}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-2 text-left" style={{ textAlign: "left", color: "#000000" }}>
              <p style={{ color: "#000000", textAlign: "left", margin: "8px 0", fontSize: "14px" }}>
                <strong>First Name:</strong> {firstName || "—"}
              </p>
              <p style={{ color: "#000000", textAlign: "left", margin: "8px 0", fontSize: "14px" }}>
                <strong>Last Name:</strong> {lastName || "—"}
              </p>
              <p style={{ color: "#000000", textAlign: "left", margin: "8px 0", fontSize: "14px" }}>
                <strong>Email:</strong> {userEmail || "—"}
              </p>
              <p style={{ color: "#000000", textAlign: "left", margin: "8px 0", fontSize: "14px" }}>
                <strong>Role:</strong> {roleLabel || "—"}
              </p>
              <button
                className="w-full mt-4 px-4 py-2 rounded-md border-none bg-black hover:bg-gray-800 text-white focus:outline-none cursor-pointer"
                onClick={() => setUpdateOpen(true)}
              >
                Edit profile
              </button>
            </div>
          )}
        </div>
      </ProfileModal>

      {/* ✅ NAVBAR — z-index 200, below sidebar (999) */}
      <header style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 200,
        backgroundColor: "black",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxSizing: "border-box",
        height: "92px",
      }}>
        {/* LEFT: hamburger + logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* ✅ Hamburger — only on mobile */}
          {isMobile && (
            <button
              onClick={onMenuClick}
              style={{
                color: "white",
                fontSize: "24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                lineHeight: 1,
                padding: "4px",
              }}
            >
              ☰
            </button>
          )}

          {/* Logo */}
          <div onClick={() => changeTab("legacy")} style={{ cursor: "pointer" }}>
            <img
              style={{ height: "48px", width: "160px", objectFit: "cover" }}
              loading="lazy"
              alt="LegacyX"
              src={LegacyLogo}
            />
          </div>
        </div>

        {/* RIGHT: avatar + menu */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={handleOpenMenu}>
            <img
              style={{
                height: "52px",
                width: "52px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
              loading="lazy"
              alt="Profile"
              src={ellipse}
            />
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleMyAccount}>My account</MenuItem>
            {can("SUPER_ADMIN_ONLY") && (
              <MenuItem
                onClick={() => {
                  handleCloseMenu();
                  navigate("/admin-management");
                }}
              >
                Admin Management
              </MenuItem>
            )}
            {can(PERMISSIONS.MANAGE_SYSTEM) && (
              <MenuItem
                onClick={() => {
                  handleCloseMenu();
                  const target = window.location.pathname.startsWith("/vortex") ? "/vortexappmanager" : "/appmanager";
                  navigate(target);
                }}
              >
                App Manager
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                navigate("/settings");
              }}
            >
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </div>
      </header>
    </>
  );
};

export default Navbar;